import { useState, useCallback, type ChangeEvent } from "react";
import { Button } from "@cieda/djayma-ui";
import { Upload, Download, AlertCircle, CheckCircle2 } from "lucide-react";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { csvRowSchema, type CsvRowValues } from "@/lib/validation/product";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CSVImportModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (rows: readonly Record<string, unknown>[]) => void;
  readonly isImporting?: boolean;
}

interface ParsedRow {
  readonly row: number;
  readonly data: CsvRowValues | null;
  readonly error: string | null;
}

// ---------------------------------------------------------------------------
// CSV Template
// ---------------------------------------------------------------------------

const CSV_TEMPLATE_HEADER =
  "name,sku,brand,category,price_fcfa,unit,weight_kg,min_order_qty,tva_rate,stock,active";
const CSV_TEMPLATE_EXAMPLE =
  "Mayonnaise Yousra 500ml,YSR-MAYO-001,yousra,sauce,2500,pièce,0.5,1,0.18,100,true";
const CSV_TEMPLATE = `${CSV_TEMPLATE_HEADER}\n${CSV_TEMPLATE_EXAMPLE}`;

function downloadTemplate() {
  const blob = new Blob([CSV_TEMPLATE], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "modele-import-produits.csv";
  link.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// CSV parsing
// ---------------------------------------------------------------------------

function parseCSV(text: string): readonly ParsedRow[] {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
  if (lines.length < 2) return [];

  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());

  return lines.slice(1).map((line, i) => {
    const values = line.split(",").map((v) => v.trim());
    const record: Record<string, string> = {};
    headers.forEach((header, idx) => {
      record[header] = values[idx] ?? "";
    });

    const result = csvRowSchema.safeParse(record);
    if (result.success) {
      return { row: i + 2, data: result.data, error: null };
    }

    const firstError = result.error.issues[0];
    return {
      row: i + 2,
      data: null,
      error: `${firstError.path.join(".")}: ${firstError.message}`,
    };
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CSVImportModal({
  open,
  onClose,
  onConfirm,
  isImporting = false,
}: CSVImportModalProps) {
  const [parsedRows, setParsedRows] = useState<readonly ParsedRow[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);

  const validRows = parsedRows.filter((r) => r.data !== null);
  const errorRows = parsedRows.filter((r) => r.error !== null);

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      setParsedRows(parseCSV(text));
    };
    reader.readAsText(file);
  }, []);

  const handleConfirm = () => {
    const rows = validRows.map((r) => r.data as Record<string, unknown>);
    onConfirm(rows);
  };

  const handleClose = () => {
    setParsedRows([]);
    setFileName(null);
    onClose();
  };

  return (
    <AdminModal open={open} onClose={handleClose} title="Importer des produits (CSV)" size="lg">
      <div className="space-y-4">
        {/* Download template */}
        <div className="flex items-center justify-between rounded-md border border-stone-200 bg-stone-50 px-4 py-3">
          <p className="text-sm text-stone-600">
            Téléchargez le modèle CSV pour le format attendu.
          </p>
          <Button variant="outline" size="sm" onClick={downloadTemplate} className="gap-1.5">
            <Download className="h-3.5 w-3.5" />
            Modèle CSV
          </Button>
        </div>

        {/* File upload */}
        <div>
          <label
            htmlFor="csv-upload"
            className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-stone-300 bg-white px-6 py-8 text-center transition-colors hover:border-amber-300 hover:bg-amber-50/30"
          >
            <Upload className="mb-2 h-8 w-8 text-stone-400" />
            <span className="text-sm font-medium text-stone-700">
              {fileName ?? "Cliquez pour sélectionner un fichier CSV"}
            </span>
            <span className="mt-1 text-xs text-stone-400">
              Format : .csv, encodage UTF-8
            </span>
          </label>
          <input
            id="csv-upload"
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="sr-only"
          />
        </div>

        {/* Preview results */}
        {parsedRows.length > 0 && (
          <div className="space-y-3">
            {/* Summary */}
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1.5 text-emerald-700">
                <CheckCircle2 className="h-4 w-4" />
                {validRows.length} valide(s)
              </span>
              {errorRows.length > 0 && (
                <span className="flex items-center gap-1.5 text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  {errorRows.length} erreur(s)
                </span>
              )}
            </div>

            {/* Error details */}
            {errorRows.length > 0 && (
              <div className="max-h-32 overflow-y-auto rounded-md border border-red-200 bg-red-50 p-3">
                <ul className="space-y-1 text-xs text-red-700">
                  {errorRows.map((r) => (
                    <li key={r.row}>
                      Ligne {r.row} : {r.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Preview table */}
            {validRows.length > 0 && (
              <div className="max-h-48 overflow-auto rounded-md border border-stone-200">
                <table className="w-full text-xs">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Nom</th>
                      <th className="px-3 py-2 text-left">SKU</th>
                      <th className="px-3 py-2 text-left">Marque</th>
                      <th className="px-3 py-2 text-right">Prix FCFA</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {validRows.slice(0, 20).map((r) => (
                      <tr key={r.row}>
                        <td className="px-3 py-1.5">{r.data!.name}</td>
                        <td className="px-3 py-1.5 font-mono">{r.data!.sku}</td>
                        <td className="px-3 py-1.5">{r.data!.brand}</td>
                        <td className="px-3 py-1.5 text-right">
                          {new Intl.NumberFormat("fr-SN").format(r.data!.price_fcfa)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {validRows.length > 20 && (
                  <p className="px-3 py-2 text-xs text-stone-400">
                    ... et {validRows.length - 20} autre(s)
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 border-t border-stone-100 pt-4">
          <Button variant="outline" onClick={handleClose}>
            Annuler
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={validRows.length === 0 || isImporting}
          >
            {isImporting
              ? "Importation..."
              : `Importer ${validRows.length} produit(s)`}
          </Button>
        </div>
      </div>
    </AdminModal>
  );
}
